import * as React from "react";
import { roundToTen } from "../../../util";

export interface IWiseLeadershipGridChartProps {
  warmth: number;
  expectations: number;
  label: string;
  /** Survey/school primary color for callout accents (optional). */
  accentColor?: string;
  /** Coach scores — plotted in red (same as old WiseLeaderCanvas). */
  coachWarmth?: number;
  coachExpectations?: number;
  /**
   * Print/PDF embed mode — uses fixed dimensions so WeasyPrint/Puppeteer
   * cannot collapse the SVG to zero height.
   */
  forPrint?: boolean;
  /**
   * When true, legend uses AD Survey labels (Coach / AD).
   * When false/undefined, legend uses Student / Coach.
   */
  isAdSurvey?: boolean;
}

const SIZE = 638;
const PAD_LEFT = 58;
const PAD_RIGHT = 28;
/** Extra top pad so title + Student/Coach legend never collide with the plot. */
const PAD_TOP = 54;
const PAD_BOTTOM = 58;
const PLOT_W = SIZE - PAD_LEFT - PAD_RIGHT;
const PLOT_H = SIZE - PAD_TOP - PAD_BOTTOM;

/** Approx glyph width for quadrant labels (bold uppercase + letter-spacing). */
function estimateTextWidth(text: string, fontSize: number) {
  // Character-aware estimate so label boxes hug text (no large empty right side).
  let width = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charAt(i);
    if (ch === " " || ch === "-") {
      width += fontSize * 0.3;
    } else if (ch === "(" || ch === ")") {
      width += fontSize * 0.36;
    } else {
      // Bold uppercase at 700 + slight letter-spacing.
      width += fontSize * 0.58;
    }
  }
  // letter-spacing: 0.02em on the rendered <text>
  width += text.length * fontSize * 0.02;
  return Math.ceil(width);
}

/** Callout width (mixed-case name + scores). Slightly wider so bold text never clips. */
function estimateCalloutWidth(text: string, fontSize: number) {
  let width = 0;
  for (const ch of text) {
    if (ch === " " || ch === "-") {
      width += fontSize * 0.34;
    } else if (ch === "/" || ch === "(" || ch === ")" || ch === ".") {
      width += fontSize * 0.4;
    } else if (ch >= "0" && ch <= "9") {
      // Bold digits (font-weight 700) need more room than thin estimates.
      width += fontSize * 0.66;
    } else if (ch >= "A" && ch <= "Z") {
      width += fontSize * 0.68;
    } else {
      width += fontSize * 0.6;
    }
  }
  return Math.ceil(width) + 2;
}

/** Elite zone ≈ 7–10 on both axes (matches reference) */
const ELITE_MIN = 7;

const STUDENT_COLOR = "#2563eb";
const COACH_COLOR = "#e11d48";
const LABEL_TEXT = "#3e2723";
const LABEL_FILL = "rgba(255, 248, 235, 0.95)";

function clampScore(n: number) {
  if (n === -1) {
    return 5;
  }
  return Math.max(0, Math.min(10, n));
}

function toX(warmth: number) {
  return PAD_LEFT + (clampScore(warmth) / 10) * PLOT_W;
}

function toY(expectations: number) {
  return PAD_TOP + ((10 - clampScore(expectations)) / 10) * PLOT_H;
}

function formatScore(n: number) {
  return n === -1 ? "—" : roundToTen(n);
}

/** Keep marker centers inset so dots/halos stay inside a rect. */
function clampPointInRect(
  x: number,
  y: number,
  left: number,
  top: number,
  right: number,
  bottom: number,
  inset: number
) {
  return {
    x: Math.max(left + inset, Math.min(right - inset, x)),
    y: Math.max(top + inset, Math.min(bottom - inset, y)),
  };
}

interface IWlgRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function circleHitsRect(
  cx: number,
  cy: number,
  radius: number,
  rect: IWlgRect,
  pad: number
) {
  const left = rect.x - pad;
  const right = rect.x + rect.w + pad;
  const top = rect.y - pad;
  const bottom = rect.y + rect.h + pad;
  const nearestX = Math.max(left, Math.min(cx, right));
  const nearestY = Math.max(top, Math.min(cy, bottom));
  const dx = cx - nearestX;
  const dy = cy - nearestY;
  return dx * dx + dy * dy < radius * radius;
}

function rectsOverlap(a: IWlgRect, b: IWlgRect, pad: number) {
  return !(
    a.x + a.w + pad <= b.x ||
    b.x + b.w + pad <= a.x ||
    a.y + a.h + pad <= b.y ||
    b.y + b.h + pad <= a.y
  );
}

/** Push a circle just outside a rect (nearest edge), keeping score position as close as possible. */
function pushCircleOutOfRect(
  cx: number,
  cy: number,
  radius: number,
  rect: IWlgRect,
  pad: number
) {
  const left = rect.x - pad - radius;
  const right = rect.x + rect.w + pad + radius;
  const top = rect.y - pad - radius;
  const bottom = rect.y + rect.h + pad + radius;
  const distLeft = Math.abs(cx - left);
  const distRight = Math.abs(cx - right);
  const distTop = Math.abs(cy - top);
  const distBottom = Math.abs(cy - bottom);
  const minDist = Math.min(distLeft, distRight, distTop, distBottom);
  if (minDist === distLeft) {
    return { x: left, y: cy };
  }
  if (minDist === distRight) {
    return { x: right, y: cy };
  }
  if (minDist === distTop) {
    return { x: cx, y: top };
  }
  return { x: cx, y: bottom };
}

function getQuadrantLabelRect(
  leftX: number,
  bottomY: number,
  line1: string,
  line2?: string,
  forPrint?: boolean,
  maxRight?: number
): IWlgRect {
  const padX = 8;
  const padTop = 10;
  const padBottom = 12;
  const fontSize1 = 12;
  const fontSize2 = 11.5;
  const lineGap = 5;
  const line1W = estimateTextWidth(line1, fontSize1);
  const line2W = line2 ? estimateTextWidth(line2, fontSize2) : 0;
  // TRANSFORMATIONAL is the widest bold line — add a small right buffer so
  // glyphs never spill past the rect edge (other labels unchanged).
  const rightBuffer = line1 === "TRANSFORMATIONAL" ? 10 : 0;
  let w = Math.max(line1W, line2W) + padX * 2 + rightBuffer;
  const x = leftX;
  // Live WeasyPrint can paint text wider than local browsers — never let
  // print boxes extend past the plot edge (screen / new-data unchanged).
  if (forPrint && maxRight != null && x + w > maxRight) {
    w = Math.max(24, maxRight - x);
  }
  const h = line2
    ? padTop + fontSize1 + lineGap + fontSize2 + padBottom
    : padTop + fontSize1 + padBottom;
  return { x, y: bottomY - h, w, h };
}

const STUDENT_DOT_R = 16;
const COACH_DOT_R = 13;
/** Extra gap beyond outer halo so dots never visually touch the score box. */
const DOT_CLEAR_PAD = 14;

/**
 * Wise Leadership Grid — matched to reference screenshot (arrow, labels, points).
 */
export class WiseLeadershipGridChart extends React.Component<
  IWiseLeadershipGridChartProps
> {
  public render() {
    const {
      warmth,
      expectations,
      label,
      coachWarmth,
      coachExpectations,
      forPrint = false,
      isAdSurvey = false,
    } = this.props;

    // AD survey uses Coach (rater / blue) + AD (self / red); regular classroom reports use Student + Teacher.
    const primaryLegendLabel = isAdSurvey ? "Coach" : "Student";
    const secondaryLegendLabel = isAdSurvey ? "AD" : "Teacher";

    const hasPoint = !(warmth === -1 && expectations === -1);
    const hasCoachPoint =
      coachWarmth !== undefined &&
      coachExpectations !== undefined &&
      !(coachWarmth === -1 && coachExpectations === -1);

    const cxRaw = toX(warmth);
    const cyRaw = toY(expectations);
    let coachCx = hasCoachPoint ? toX(coachWarmth as number) : 0;
    let coachCy = hasCoachPoint ? toY(coachExpectations as number) : 0;
    const coachInElite =
      hasCoachPoint &&
      clampScore(coachWarmth as number) >= ELITE_MIN &&
      clampScore(coachExpectations as number) >= ELITE_MIN;

    const eliteX = toX(ELITE_MIN);
    const eliteY = toY(10);
    const eliteW = toX(10) - eliteX;
    const eliteH = toY(ELITE_MIN) - eliteY;
    const eliteRight = eliteX + eliteW;
    const eliteBottom = eliteY + eliteH;
    const plotRight = PAD_LEFT + PLOT_W;
    const plotBottom = PAD_TOP + PLOT_H;

    const midX = toX(5);
    const midY = toY(5);

    // Bottom-aligned label pairs; boxes sit on the LEFT of each quadrant.
    const topLabelBottom = midY - 8;
    const bottomLabelBottom = PAD_TOP + PLOT_H - 10;
    const leftQuadLeft = PAD_LEFT + 14;
    const rightQuadLeft = midX + 14;

    const plotRightEdge = plotRight - 8;
    const quadrantLabelRects: IWlgRect[] = [
      getQuadrantLabelRect(
        leftQuadLeft,
        topLabelBottom,
        "TRANSACTIONAL",
        "(AUTHORITATIVE) LEADER",
        forPrint,
        plotRightEdge
      ),
      getQuadrantLabelRect(
        rightQuadLeft,
        topLabelBottom,
        "TRANSFORMATIONAL",
        "(EMPOWERING) LEADER",
        forPrint,
        plotRightEdge
      ),
      getQuadrantLabelRect(
        leftQuadLeft,
        bottomLabelBottom,
        "NEGLECTFUL",
        "LEADER",
        forPrint,
        plotRightEdge
      ),
      getQuadrantLabelRect(
        rightQuadLeft,
        bottomLabelBottom,
        "PERMISSIVE",
        "(FRIEND) LEADER",
        forPrint,
        plotRightEdge
      ),
    ];

    const scoreLine = `(${formatScore(warmth)} / ${formatScore(expectations)})`;
    // Two-line callout: slightly larger type, width hugs text (no extra right gap).
    const calloutPadX = 8;
    const calloutPadY = 5;
    const nameFontSize = 13;
    const scoreFontSize = 12;
    const calloutW =
      Math.max(
        estimateCalloutWidth(label, nameFontSize),
        estimateCalloutWidth(scoreLine, scoreFontSize)
      ) +
      calloutPadX * 2;
    const calloutLineGap = 2;
    const calloutH =
      calloutPadY + nameFontSize + calloutLineGap + scoreFontSize + calloutPadY;

    const clampDotInPlot = (
      x: number,
      y: number,
      inset: number,
      preferElite: boolean
    ) => {
      if (preferElite) {
        return clampPointInRect(
          x,
          y,
          eliteX,
          eliteY,
          eliteRight,
          eliteBottom,
          inset
        );
      }
      return clampPointInRect(
        x,
        y,
        PAD_LEFT,
        PAD_TOP,
        plotRight,
        plotBottom,
        inset
      );
    };

    const clearDotFromRects = (
      x: number,
      y: number,
      radius: number,
      obstacles: IWlgRect[],
      preferElite: boolean
    ) => {
      let nextX = x;
      let nextY = y;
      let pass = 0;
      while (pass < 16) {
        let moved = false;
        for (const obstacle of obstacles) {
          if (
            circleHitsRect(
              nextX,
              nextY,
              radius,
              obstacle,
              DOT_CLEAR_PAD
            )
          ) {
            const pushed = pushCircleOutOfRect(
              nextX,
              nextY,
              radius,
              obstacle,
              DOT_CLEAR_PAD
            );
            let clamped = clampDotInPlot(
              pushed.x,
              pushed.y,
              radius + 2,
              preferElite
            );
            // Elite clamp can put the dot back onto the score box — escape to full plot.
            if (
              preferElite &&
              circleHitsRect(
                clamped.x,
                clamped.y,
                radius,
                obstacle,
                DOT_CLEAR_PAD
              )
            ) {
              clamped = clampDotInPlot(
                pushed.x,
                pushed.y,
                radius + 2,
                false
              );
            }
            nextX = clamped.x;
            nextY = clamped.y;
            moved = true;
          }
        }
        if (!moved) {
          break;
        }
        pass += 1;
      }
      return { x: nextX, y: nextY };
    };

    const clampCalloutInPlot = (box: IWlgRect): IWlgRect => {
      const x = Math.max(
        PAD_LEFT + 8,
        Math.min(box.x, plotRight - box.w - 8)
      );
      const y = Math.max(
        PAD_TOP + 8,
        Math.min(box.y, plotBottom - box.h - 8)
      );
      return { x, y, w: box.w, h: box.h };
    };

    /** Move score box off a circle (prefer shifting the callout, not the score point). */
    const pushCalloutAwayFromDot = (
      box: IWlgRect,
      dotX: number,
      dotY: number,
      radius: number
    ): IWlgRect => {
      if (!circleHitsRect(dotX, dotY, radius, box, DOT_CLEAR_PAD)) {
        return box;
      }
      const pushed = pushCircleOutOfRect(
        dotX,
        dotY,
        radius,
        box,
        DOT_CLEAR_PAD
      );
      return clampCalloutInPlot({
        x: box.x - (pushed.x - dotX),
        y: box.y - (pushed.y - dotY),
        w: box.w,
        h: box.h,
      });
    };

    // Keep student fully inside the plot (score 10 used to overflow into the header).
    let studentPoint = clampPointInRect(
      cxRaw,
      cyRaw,
      PAD_LEFT,
      PAD_TOP,
      plotRight,
      plotBottom,
      STUDENT_DOT_R
    );
    let cx = studentPoint.x;
    let cy = studentPoint.y;

    // If dots overlap, nudge coach inside the plot — never outside / into the legend.
    if (hasCoachPoint && hasPoint) {
      const dist = Math.hypot(coachCx - cx, coachCy - cy);
      if (dist < 22) {
        coachCx = cx - 24;
        coachCy = cy - 18;
      }
    }

    // Red coach dot: when scores are elite, keep it clearly inside the Elite Zone box.
    if (hasCoachPoint) {
      if (coachInElite) {
        const clamped = clampPointInRect(
          coachCx,
          coachCy,
          eliteX,
          eliteY,
          eliteRight,
          eliteBottom,
          COACH_DOT_R
        );
        coachCx = clamped.x;
        coachCy = clamped.y;
      } else {
        const clamped = clampPointInRect(
          coachCx,
          coachCy,
          PAD_LEFT,
          PAD_TOP,
          plotRight,
          plotBottom,
          COACH_DOT_R
        );
        coachCx = clamped.x;
        coachCy = clamped.y;
      }
    }

    // First pass: keep both dots clear of the 4 quadrant label boxes.
    studentPoint = clearDotFromRects(
      cx,
      cy,
      STUDENT_DOT_R,
      quadrantLabelRects,
      false
    );
    cx = studentPoint.x;
    cy = studentPoint.y;
    if (hasCoachPoint) {
      const coachCleared = clearDotFromRects(
        coachCx,
        coachCy,
        COACH_DOT_R,
        quadrantLabelRects,
        coachInElite
      );
      coachCx = coachCleared.x;
      coachCy = coachCleared.y;
    }

    // Score bubble candidates: near student, left of elite, then a dense plot scan.
    const leftOfEliteX = Math.max(PAD_LEFT + 8, eliteX - calloutW - 16);
    const calloutCandidates: Array<{ x: number; y: number }> = [
      { x: cx + STUDENT_DOT_R + 22, y: cy - calloutH / 2 },
      { x: cx - calloutW - STUDENT_DOT_R - 22, y: cy - calloutH / 2 },
      { x: cx - calloutW / 2, y: cy - calloutH - STUDENT_DOT_R - 20 },
      { x: cx - calloutW / 2, y: cy + STUDENT_DOT_R + 20 },
      { x: leftOfEliteX, y: Math.max(PAD_TOP + 8, cy - calloutH / 2) },
      { x: leftOfEliteX, y: eliteY + 36 },
      { x: leftOfEliteX, y: eliteBottom - calloutH - 8 },
      { x: PAD_LEFT + 10, y: PAD_TOP + 10 },
      { x: midX - calloutW - 12, y: midY - calloutH - 12 },
      { x: midX + 12, y: midY - calloutH - 12 },
      { x: plotRight - calloutW - 8, y: PAD_TOP + 10 },
      { x: plotRight - calloutW - 8, y: plotBottom - calloutH - 8 },
      { x: PAD_LEFT + 10, y: plotBottom - calloutH - 8 },
    ];
    for (let y = PAD_TOP + 8; y <= plotBottom - calloutH - 8; y += 14) {
      calloutCandidates.push({ x: leftOfEliteX, y });
      calloutCandidates.push({ x: PAD_LEFT + 10, y });
      calloutCandidates.push({
        x: Math.max(PAD_LEFT + 8, midX - calloutW - 12),
        y,
      });
      calloutCandidates.push({ x: Math.min(midX + 12, plotRight - calloutW - 8), y });
    }

    const calloutHitsObstacle = (box: IWlgRect) => {
      if (
        hasPoint &&
        circleHitsRect(cx, cy, STUDENT_DOT_R, box, DOT_CLEAR_PAD)
      ) {
        return true;
      }
      if (
        hasCoachPoint &&
        circleHitsRect(coachCx, coachCy, COACH_DOT_R, box, DOT_CLEAR_PAD)
      ) {
        return true;
      }
      for (const labelRect of quadrantLabelRects) {
        if (rectsOverlap(box, labelRect, 6)) {
          return true;
        }
      }
      return false;
    };

    let calloutX = leftOfEliteX;
    let calloutY = Math.max(PAD_TOP + 8, Math.min(cy - calloutH / 2, plotBottom - calloutH - 8));
    let placedCallout = false;
    for (const candidate of calloutCandidates) {
      const box = clampCalloutInPlot({
        x: candidate.x,
        y: candidate.y,
        w: calloutW,
        h: calloutH,
      });
      if (!calloutHitsObstacle(box)) {
        calloutX = box.x;
        calloutY = box.y;
        placedCallout = true;
        break;
      }
    }
    if (!placedCallout) {
      let box = clampCalloutInPlot({
        x: leftOfEliteX,
        y: calloutY,
        w: calloutW,
        h: calloutH,
      });
      // Prefer shifting the score box off the dots before moving score points.
      if (hasPoint) {
        box = pushCalloutAwayFromDot(box, cx, cy, STUDENT_DOT_R);
      }
      if (hasCoachPoint) {
        box = pushCalloutAwayFromDot(box, coachCx, coachCy, COACH_DOT_R);
      }
      if (hasPoint) {
        box = pushCalloutAwayFromDot(box, cx, cy, STUDENT_DOT_R);
      }
      calloutX = box.x;
      calloutY = box.y;
    }

    const calloutRect: IWlgRect = {
      x: calloutX,
      y: calloutY,
      w: calloutW,
      h: calloutH,
    };

    // Final pass: dots must never sit on the dynamic score box or the 4 labels.
    // Do not force coach back into elite here — that re-creates callout overlaps.
    const finalObstacles = quadrantLabelRects.concat([calloutRect]);
    studentPoint = clearDotFromRects(
      cx,
      cy,
      STUDENT_DOT_R,
      finalObstacles,
      false
    );
    cx = studentPoint.x;
    cy = studentPoint.y;
    if (hasCoachPoint) {
      const coachCleared = clearDotFromRects(
        coachCx,
        coachCy,
        COACH_DOT_R,
        finalObstacles,
        false
      );
      coachCx = coachCleared.x;
      coachCy = coachCleared.y;
    }

    // If coach still overlaps student after nudges, separate again inside plot.
    if (hasCoachPoint && hasPoint) {
      const dist = Math.hypot(coachCx - cx, coachCy - cy);
      if (dist < STUDENT_DOT_R + COACH_DOT_R + 4) {
        coachCx = cx - 26;
        coachCy = cy - 20;
        const coachCleared = clearDotFromRects(
          coachCx,
          coachCy,
          COACH_DOT_R,
          finalObstacles,
          false
        );
        coachCx = coachCleared.x;
        coachCy = coachCleared.y;
      }
    }

    // Re-pick callout after final dot positions so score box never covers either dot.
    {
      const nearStudent = [
        { x: cx + STUDENT_DOT_R + 22, y: cy - calloutH / 2 },
        { x: cx - calloutW - STUDENT_DOT_R - 22, y: cy - calloutH / 2 },
        { x: cx - calloutW / 2, y: cy - calloutH - STUDENT_DOT_R - 20 },
        { x: cx - calloutW / 2, y: cy + STUDENT_DOT_R + 20 },
        { x: leftOfEliteX, y: Math.max(PAD_TOP + 8, cy - calloutH / 2) },
        { x: leftOfEliteX, y: eliteY + 36 },
        { x: PAD_LEFT + 10, y: PAD_TOP + 10 },
        { x: midX - calloutW - 12, y: midY - calloutH - 12 },
      ];
      for (let y = PAD_TOP + 8; y <= plotBottom - calloutH - 8; y += 12) {
        nearStudent.push({ x: leftOfEliteX, y });
        nearStudent.push({ x: PAD_LEFT + 10, y });
      }
      let refreshedPlaced = false;
      for (const candidate of nearStudent) {
        const next = clampCalloutInPlot({
          x: candidate.x,
          y: candidate.y,
          w: calloutW,
          h: calloutH,
        });
        if (!calloutHitsObstacle(next)) {
          calloutX = next.x;
          calloutY = next.y;
          refreshedPlaced = true;
          break;
        }
      }
      if (!refreshedPlaced) {
        let box = clampCalloutInPlot({
          x: calloutX,
          y: calloutY,
          w: calloutW,
          h: calloutH,
        });
        if (hasPoint) {
          box = pushCalloutAwayFromDot(box, cx, cy, STUDENT_DOT_R);
        }
        if (hasCoachPoint) {
          box = pushCalloutAwayFromDot(box, coachCx, coachCy, COACH_DOT_R);
        }
        if (hasPoint) {
          box = pushCalloutAwayFromDot(box, cx, cy, STUDENT_DOT_R);
        }
        calloutX = box.x;
        calloutY = box.y;
      }
    }

    // Last safety: if a dot still hits the chosen callout, push the dot out again.
    const safeCalloutRect: IWlgRect = {
      x: calloutX,
      y: calloutY,
      w: calloutW,
      h: calloutH,
    };
    const safeObstacles = quadrantLabelRects.concat([safeCalloutRect]);
    studentPoint = clearDotFromRects(
      cx,
      cy,
      STUDENT_DOT_R,
      safeObstacles,
      false
    );
    cx = studentPoint.x;
    cy = studentPoint.y;
    if (hasCoachPoint) {
      const coachCleared = clearDotFromRects(
        coachCx,
        coachCy,
        COACH_DOT_R,
        safeObstacles,
        false
      );
      coachCx = coachCleared.x;
      coachCy = coachCleared.y;
    }

    // Absolute last check: if still overlapping, shift callout once more then dots.
    {
      let box = { x: calloutX, y: calloutY, w: calloutW, h: calloutH };
      if (hasPoint && circleHitsRect(cx, cy, STUDENT_DOT_R, box, DOT_CLEAR_PAD)) {
        box = pushCalloutAwayFromDot(box, cx, cy, STUDENT_DOT_R);
      }
      if (
        hasCoachPoint &&
        circleHitsRect(coachCx, coachCy, COACH_DOT_R, box, DOT_CLEAR_PAD)
      ) {
        box = pushCalloutAwayFromDot(box, coachCx, coachCy, COACH_DOT_R);
      }
      calloutX = box.x;
      calloutY = box.y;
      const lastObstacles = quadrantLabelRects.concat([box]);
      if (hasPoint) {
        studentPoint = clearDotFromRects(
          cx,
          cy,
          STUDENT_DOT_R,
          lastObstacles,
          false
        );
        cx = studentPoint.x;
        cy = studentPoint.y;
      }
      if (hasCoachPoint) {
        const coachCleared = clearDotFromRects(
          coachCx,
          coachCy,
          COACH_DOT_R,
          lastObstacles,
          false
        );
        coachCx = coachCleared.x;
        coachCy = coachCleared.y;
      }
    }

    // Leader line from callout toward the student point.
    const calloutAnchorX =
      calloutX + calloutW / 2 < cx ? calloutX + calloutW : calloutX;
    const calloutAnchorY =
      calloutY + calloutH < cy ? calloutY + calloutH : calloutY;

    const ticks = [0, 2, 4, 6, 8, 10];
    const fineTicks = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

    // White growth arrow: attached to blue (student) dot, tip at Elite Zone center.
    const tipX = eliteX + eliteW * 0.5;
    const tipY = eliteY + eliteH * 0.5;
    const arrowStroke = 3.5;
    const headLen = 13;
    const headHalf = 7;
    // Start on the outer edge of the blue marker halo, then curve up-right.
    const roughToTipDx = tipX - cx;
    const roughToTipDy = tipY - cy;
    const roughToTipLen = Math.max(1, Math.hypot(roughToTipDx, roughToTipDy));
    const rimOffset = 15;
    const arrowStartX = cx + (roughToTipDx / roughToTipLen) * rimOffset;
    const arrowStartY = cy + (roughToTipDy / roughToTipLen) * rimOffset;
    const roughDx = tipX - arrowStartX;
    const roughDy = tipY - arrowStartY;
    const roughLen = Math.max(1, Math.hypot(roughDx, roughDy));

    // Gentle bow (same silhouette as reference screenshot).
    const midChordX = (arrowStartX + tipX) / 2;
    const midChordY = (arrowStartY + tipY) / 2;
    const roughUx = roughDx / roughLen;
    const roughUy = roughDy / roughLen;
    const bow = Math.min(32, roughLen * 0.3);
    const ctrlX = midChordX - roughUy * bow;
    const ctrlY = midChordY + roughUx * bow;

    // Head follows end-tangent of the quadratic (ctrl → tip).
    const tdx = tipX - ctrlX;
    const tdy = tipY - ctrlY;
    const tlen = Math.max(1, Math.hypot(tdx, tdy));
    const ux = tdx / tlen;
    const uy = tdy / tlen;
    const baseMidX = tipX - ux * headLen;
    const baseMidY = tipY - uy * headLen;
    const leftX = baseMidX - uy * headHalf;
    const leftY = baseMidY + ux * headHalf;
    const rightX = baseMidX + uy * headHalf;
    const rightY = baseMidY - ux * headHalf;
    const arrowPath = `M ${arrowStartX} ${arrowStartY} Q ${ctrlX} ${ctrlY} ${baseMidX} ${baseMidY}`;
    const studentInElite =
      hasPoint &&
      clampScore(warmth) >= ELITE_MIN &&
      clampScore(expectations) >= ELITE_MIN;
    // White arrow only when blue (student) point is outside Elite Zone.
    const showArrow = hasPoint && !studentInElite && roughLen > 12;

    return (
      <div
        className="wlg-chart-wrap"
        style={
          forPrint
            ? {
                boxSizing: "border-box",
                height: "3.125in",
                lineHeight: 0,
                maxWidth: "3.125in",
                overflow: "hidden",
                width: "3.125in",
              }
            : {
                boxSizing: "border-box",
                lineHeight: 0,
                maxWidth: "100%",
                width: "100%",
              }
        }
      >
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          width={forPrint ? 300 : SIZE}
          height={forPrint ? 300 : SIZE}
          preserveAspectRatio="xMidYMid meet"
          style={
            forPrint
              ? {
                  display: "block",
                  fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
                  height: "3.125in",
                  overflow: "hidden",
                  width: "3.125in",
                }
              : {
                  display: "block",
                  fontFamily:
                    "system-ui, -apple-system, 'Segoe UI', sans-serif",
                  height: "auto",
                  maxWidth: "100%",
                  width: "100%",
                }
          }
          role="img"
          aria-label={`Wise Leadership Grid: ${label} ${scoreLine}`}
        >
          <defs>
            <linearGradient id="wlgHeat" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#e53935" />
              <stop offset="22%" stopColor="#fb8c00" />
              <stop offset="42%" stopColor="#fdd835" />
              <stop offset="62%" stopColor="#c0ca33" />
              <stop offset="82%" stopColor="#66bb6a" />
              <stop offset="100%" stopColor="#43a047" />
            </linearGradient>

            <filter id="wlgHalo" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur
                in="SourceAlpha"
                stdDeviation="3.2"
                result="blur"
              />
              <feFlood floodColor="#ffffff" floodOpacity="0.95" result="color" />
              <feComposite
                in="color"
                in2="blur"
                operator="in"
                result="glow"
              />
              <feMerge>
                <feMergeNode in="glow" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            <filter id="wlgSoft" x="-30%" y="-30%" width="160%" height="160%">
              <feDropShadow
                dx="0"
                dy="1.5"
                stdDeviation="1.8"
                floodColor="#000000"
                floodOpacity="0.18"
              />
            </filter>

            <clipPath id="wlgPlotClip">
              <rect
                x={PAD_LEFT}
                y={PAD_TOP}
                width={PLOT_W}
                height={PLOT_H}
                rx="4"
              />
            </clipPath>
          </defs>

          <rect
            x="0.5"
            y="0.5"
            width={SIZE - 1}
            height={SIZE - 1}
            rx="12"
            fill="#ffffff"
            stroke="#d1d5db"
            strokeWidth="1.5"
          />

          <text
            x={SIZE / 2}
            y="20"
            textAnchor="middle"
            fill="#1f2937"
            fontSize="16"
            fontWeight="700"
          >
            Wise Leadership Grid
          </text>

          <rect
            x={PAD_LEFT}
            y={PAD_TOP}
            width={PLOT_W}
            height={PLOT_H}
            rx="4"
            fill="url(#wlgHeat)"
          />

          <g stroke="rgba(255,255,255,0.22)" strokeWidth="1">
            {fineTicks.map((t) => (
              <g key={`f-${t}`}>
                <line
                  x1={toX(t)}
                  y1={PAD_TOP}
                  x2={toX(t)}
                  y2={PAD_TOP + PLOT_H}
                />
                <line
                  x1={PAD_LEFT}
                  y1={toY(t)}
                  x2={PAD_LEFT + PLOT_W}
                  y2={toY(t)}
                />
              </g>
            ))}
          </g>

          {/* Elite zone + dark badge (reference) */}
          <g clipPath="url(#wlgPlotClip)">
            <rect
              x={eliteX}
              y={eliteY}
              width={eliteW}
              height={eliteH}
              rx="4"
              fill="#1b7a3d"
              opacity="0.82"
              stroke="rgba(255,255,255,0.7)"
              strokeWidth="1.75"
            />
            <rect
              x={eliteX + 10}
              y={eliteY + 8}
              width="118"
              height="22"
              rx="11"
              fill="#14532d"
            />
            <text
              x={eliteX + 22}
              y={eliteY + 23}
              textAnchor="start"
              fill="#ffffff"
              fontSize="11"
              fontWeight="700"
              letterSpacing="0.04em"
            >
              ★ ELITE ZONE
            </text>
          </g>

          <line
            x1={midX}
            y1={PAD_TOP}
            x2={midX}
            y2={PAD_TOP + PLOT_H}
            stroke="#ffffff"
            strokeWidth="2.5"
            strokeDasharray="8 6"
            strokeLinecap="round"
          />
          <line
            x1={PAD_LEFT}
            y1={midY}
            x2={PAD_LEFT + PLOT_W}
            y2={midY}
            stroke="#ffffff"
            strokeWidth="2.5"
            strokeDasharray="8 6"
            strokeLinecap="round"
          />

          {/* Four labels — left side of each quadrant, larger type */}
          {this.renderQuadrantLabel(
            leftQuadLeft,
            topLabelBottom,
            "TRANSACTIONAL",
            "(AUTHORITATIVE) LEADER",
            LABEL_TEXT,
            forPrint,
            plotRight - 8
          )}
          {this.renderQuadrantLabel(
            rightQuadLeft,
            topLabelBottom,
            "TRANSFORMATIONAL",
            "(EMPOWERING) LEADER",
            "#1b5e20",
            forPrint,
            plotRight - 8
          )}
          {this.renderQuadrantLabel(
            leftQuadLeft,
            bottomLabelBottom,
            "NEGLECTFUL",
            "LEADER",
            LABEL_TEXT,
            forPrint,
            plotRight - 8
          )}
          {this.renderQuadrantLabel(
            rightQuadLeft,
            bottomLabelBottom,
            "PERMISSIVE",
            "(FRIEND) LEADER",
            LABEL_TEXT,
            forPrint,
            plotRight - 8
          )}

          {ticks.map((t) => (
            <g key={`t-${t}`} fill="#6b7280" fontSize="13" fontWeight="500">
              <text x={PAD_LEFT - 12} y={toY(t) + 4} textAnchor="end">
                {t}
              </text>
              <text
                x={toX(t)}
                y={PAD_TOP + PLOT_H + 22}
                textAnchor="middle"
              >
                {t}
              </text>
            </g>
          ))}

          <text
            x={16}
            y={PAD_TOP + PLOT_H / 2}
            textAnchor="middle"
            fill="#374151"
            fontSize="15"
            fontWeight="600"
            transform={`rotate(-90 16 ${PAD_TOP + PLOT_H / 2})`}
          >
            Expectations
          </text>
          <text
            x={PAD_LEFT + PLOT_W / 2}
            y={SIZE - 14}
            textAnchor="middle"
            fill="#374151"
            fontSize="15"
            fontWeight="600"
          >
            Relational Warmth
          </text>

          {hasCoachPoint ? (
            <g>
              <circle
                cx={coachCx}
                cy={coachCy}
                r="13"
                fill={COACH_COLOR}
                opacity="0.22"
                filter="url(#wlgHalo)"
              />
              <circle
                cx={coachCx}
                cy={coachCy}
                r="9"
                fill={COACH_COLOR}
                stroke="#ffffff"
                strokeWidth="2.5"
              />
            </g>
          ) : null}

          {hasPoint ? (
            <g>
              {/* Arrow from blue student dot toward Elite Zone top-right */}
              {showArrow ? (
                <g>
                  <path
                    d={arrowPath}
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth={arrowStroke}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <polygon
                    points={`${tipX},${tipY} ${leftX},${leftY} ${rightX},${rightY}`}
                    fill="#ffffff"
                  />
                </g>
              ) : null}

              <line
                className="wlg-point-callout"
                data-wlg-callout-line="1"
                x1={cx}
                y1={cy}
                x2={calloutAnchorX}
                y2={calloutAnchorY}
                stroke={STUDENT_COLOR}
                strokeWidth="1.5"
              />

              <g className="wlg-point-callout" filter="url(#wlgSoft)">
                <rect
                  data-wlg-callout-box="1"
                  x={calloutX}
                  y={calloutY}
                  width={calloutW}
                  height={calloutH}
                  rx="6"
                  fill="#ffffff"
                  stroke={STUDENT_COLOR}
                  strokeWidth="1.5"
                />
                <text
                  data-wlg-callout-text="1"
                  x={calloutX + calloutPadX}
                  y={calloutY + calloutPadY + nameFontSize - 2}
                  textAnchor="start"
                  fill={STUDENT_COLOR}
                  fontSize={nameFontSize}
                  fontWeight="700"
                >
                  {label}
                </text>
                <text
                  data-wlg-callout-text="1"
                  x={calloutX + calloutPadX}
                  y={
                    calloutY +
                    calloutPadY +
                    nameFontSize +
                    calloutLineGap +
                    scoreFontSize -
                    2
                  }
                  textAnchor="start"
                  fill={STUDENT_COLOR}
                  fontSize={scoreFontSize}
                  fontWeight="700"
                >
                  {scoreLine}
                </text>
              </g>

              <circle
                cx={cx}
                cy={cy}
                r="16"
                fill={STUDENT_COLOR}
                opacity="0.2"
                filter="url(#wlgHalo)"
              />
              <circle
                cx={cx}
                cy={cy}
                r="11"
                fill={STUDENT_COLOR}
                stroke="#ffffff"
                strokeWidth="3.5"
                filter="url(#wlgHalo)"
              />
            </g>
          ) : null}

          {hasCoachPoint ? (
            <g fontSize="12" fontWeight="700">
              {/* Student / Coach legend — clear vertical gap, fully above plot */}
              <text x={SIZE - 30} y={14} fill="#111827" textAnchor="end">
                {primaryLegendLabel}:
              </text>
              <rect
                x={SIZE - 24}
                y={4}
                width="12"
                height="12"
                rx="2"
                fill={STUDENT_COLOR}
              />
              <text x={SIZE - 30} y={40} fill="#111827" textAnchor="end">
                {secondaryLegendLabel}:
              </text>
              <rect
                x={SIZE - 24}
                y={30}
                width="12"
                height="12"
                rx="2"
                fill={COACH_COLOR}
              />
            </g>
          ) : null}
        </svg>
      </div>
    );
  }

  /** Left-anchored label box sized tightly to text (no extra right padding). */
  private renderQuadrantLabel(
    leftX: number,
    bottomY: number,
    line1: string,
    line2?: string,
    textColor: string = LABEL_TEXT,
    forPrint: boolean = false,
    maxRight?: number
  ) {
    const padX = 8;
    const padTop = 10;
    const fontSize1 = 12;
    const fontSize2 = 11.5;
    const lineGap = 5;
    const rect = getQuadrantLabelRect(
      leftX,
      bottomY,
      line1,
      line2,
      forPrint,
      maxRight
    );
    const w = rect.w;
    const h = rect.h;
    const top = rect.y;
    const textX = rect.x + padX;
    const line1Y = top + padTop + fontSize1 * 0.85;
    const line2Y = line1Y + lineGap + fontSize2 * 0.95;
    // Force glyph run inside the box on WeasyPrint (live PDF), where fonts
    // often paint wider than local Chrome / system-ui.
    const textFitW = Math.max(8, w - padX * 2);
    const printTextProps = forPrint
      ? {
          fontFamily: "Arial, Helvetica, sans-serif",
          lengthAdjust: "spacingAndGlyphs" as "spacingAndGlyphs",
          textLength: textFitW,
        }
      : {
          letterSpacing: "0.02em",
        };
    return (
      <g
        filter={forPrint ? undefined : "url(#wlgSoft)"}
        clipPath={forPrint ? "url(#wlgPlotClip)" : undefined}
      >
        <rect
          x={rect.x}
          y={top}
          width={w}
          height={h}
          rx="8"
          fill={LABEL_FILL}
          stroke="rgba(255, 224, 178, 0.95)"
          strokeWidth="1"
        />
        <text
          x={textX}
          y={line1Y}
          textAnchor="start"
          fill={textColor}
          fontSize={fontSize1}
          fontWeight="700"
          {...printTextProps}
        >
          {line1}
        </text>
        {line2 ? (
          <text
            x={textX}
            y={line2Y}
            textAnchor="start"
            fill={textColor}
            fontSize={fontSize2}
            fontWeight="700"
            {...printTextProps}
          >
            {line2}
          </text>
        ) : null}
      </g>
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/director/EvaluationPage/WiseLeadershipGridChart.tsx